import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import Fuse from 'fuse.js';
import { Food, CarbUnit, UserPreferences } from '../types';
import { SearchResults } from './SearchResults';
import { AdvancedOptions } from './AdvancedOptions';
import { usePreferences } from '../contexts/PreferencesContext';
import './SearchBar.css';

// Lazy load BarcodeScanner component since it's heavy and not always used
const BarcodeScanner = lazy(() => import('./BarcodeScanner').then(module => ({ default: module.BarcodeScanner })));

interface SearchBarProps {
  foods: Food[];
  preferences: UserPreferences;
  onUnitChange: (unit: CarbUnit) => void;
  onSearchStarted?: () => void;
  onSearchCleared?: () => void;
  onShowFPUChange: (show: boolean) => void;
  clearSearch?: boolean;
  onClearComplete?: () => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ 
  foods, 
  preferences,
  onUnitChange,
  onSearchStarted,
  onSearchCleared,
  onShowFPUChange,
  clearSearch,
  onClearComplete
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Food[]>([]);
  const [displayedResults, setDisplayedResults] = useState<Food[]>([]);
  const [customPortionGrams, setCustomPortionGrams] = useState<number | undefined>(undefined);
  const [resultsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [isCameraAvailable, setIsCameraAvailable] = useState(true);
  const { isFavorite, getCustomFoods, getRecipes } = usePreferences();
  
  // Cache for search results to improve performance
  const searchCache = useRef(new Map<string, Food[]>());
  const maxCacheSize = 50; // Cache up to 50 queries
  
  // Pre-computed word index for faster search filtering
  const wordIndex = useRef(new Map<string, Set<string>>());
  
  // Build word index for faster search filtering
  const buildWordIndex = useCallback((foods: Food[]) => {
    const index = new Map<string, Set<string>>();
    
    foods.forEach(food => {
      const searchableNames = [
        food.nameEN,
        food.nameDE
      ];
      
      searchableNames.forEach(name => {
        if (name) {
          // Extract words and create index entries
          const words = name.toLowerCase()
            .replace(/[^\w\s]/g, ' ') // Replace special chars with spaces
            .split(/\s+/)
            .filter(word => word.length > 1); // Skip single chars
          
          words.forEach(word => {
            if (!index.has(word)) {
              index.set(word, new Set());
            }
            index.get(word)!.add(food.id);
          });
        }
      });
    });
    
    wordIndex.current = index;
  }, []);
  
  // Use word index to pre-filter foods for better performance
  const getPreFilteredFoods = useCallback((query: string, allFoods: Food[]) => {
    if (!query || query.length < 2 || wordIndex.current.size === 0) {
      return allFoods;
    }
    
    const queryWords = query.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 1);
    
    if (queryWords.length === 0) return allFoods;
    
    // For multi-word queries, we want to be more selective
    if (queryWords.length > 1) {
      // Score foods based on how many query words they match
      const foodScores = new Map<string, number>();
      
      queryWords.forEach(word => {
        // Exact word matches (highest score)
        if (wordIndex.current.has(word)) {
          wordIndex.current.get(word)!.forEach(id => {
            foodScores.set(id, (foodScores.get(id) || 0) + 3);
          });
        }
        
        // Prefix matches (medium score)
        wordIndex.current.forEach((foodIds, indexWord) => {
          if (indexWord.startsWith(word) && indexWord !== word) {
            foodIds.forEach(id => {
              foodScores.set(id, (foodScores.get(id) || 0) + 2);
            });
          }
        });
        
        // Substring matches for compound words (lowest score)
        wordIndex.current.forEach((foodIds, indexWord) => {
          if (indexWord.includes(word) && !indexWord.startsWith(word)) {
            foodIds.forEach(id => {
              foodScores.set(id, (foodScores.get(id) || 0) + 1);
            });
          }
        });
      });
      
      // Only include foods that match at least half of the query words
      const minScore = Math.ceil(queryWords.length / 2);
      const matchingFoodIds = new Set<string>();
      foodScores.forEach((score, id) => {
        if (score >= minScore) {
          matchingFoodIds.add(id);
        }
      });
      
      return allFoods.filter(food => matchingFoodIds.has(food.id));
    } else {
      // Single word query - be more inclusive
      const matchingFoodIds = new Set<string>();
      const word = queryWords[0];
      
      // Exact word matches
      if (wordIndex.current.has(word)) {
        wordIndex.current.get(word)!.forEach(id => matchingFoodIds.add(id));
      }
      
      // Prefix matches for partial words
      wordIndex.current.forEach((foodIds, indexWord) => {
        if (indexWord.startsWith(word)) {
          foodIds.forEach(id => matchingFoodIds.add(id));
        }
      });
      
      // Substring matches for compound words (e.g., "brot" in "Butterbrot")
      wordIndex.current.forEach((foodIds, indexWord) => {
        if (indexWord.includes(word) && !indexWord.startsWith(word)) {
          foodIds.forEach(id => matchingFoodIds.add(id));
        }
      });
      
      return allFoods.filter(food => matchingFoodIds.has(food.id));
    }
  }, []);
  
  // Debounce search input to reduce search frequency
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [query]);
  
  // Handle clearSearch prop
  useEffect(() => {
    if (clearSearch) {
      setQuery('');
      setDebouncedQuery('');
      setSearchResults([]);
      setDisplayedResults([]);
      setCurrentPage(1);
      onClearComplete?.();
    }
  }, [clearSearch, onClearComplete]);
  
  // Handle keyboard visibility and scroll input into view
  useEffect(() => {
    const handleResize = () => {
      // Detect if keyboard is likely open by checking viewport height change
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const isKeyboardOpen = viewportHeight < window.screen.height * 0.75;
      
      if (isKeyboardOpen) {
        // Small delay to ensure the keyboard animation is complete
        setTimeout(() => {
          // Check if any modal is open (they all use .modal-backdrop class)
          const modalOpen = document.querySelector('.modal-backdrop') !== null;
          if (modalOpen) {
            // Don't scroll if a modal is open
            return;
          }
          
          const searchInput = document.querySelector('.search-input') as HTMLElement;
          // Only scroll if the search input is actually focused
          // This prevents scrolling when typing in other inputs (like recipe modal)
          if (searchInput && document.activeElement === searchInput) {
            searchInput.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100);
      }
    };

    // Listen for viewport changes (includes keyboard open/close)
    if (window.visualViewport) {
      const viewport = window.visualViewport;
      viewport.addEventListener('resize', handleResize);
      return () => viewport.removeEventListener('resize', handleResize);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);


  // Food ranking function - FIXED for favorites and multi-word search
  const rankFood = useCallback((result: any, query: string) => {
    const food = result.item;
    const fuseScore = result.score || 0; // Lower is better for Fuse.js
    const foodNameEN = (food.nameEN || '').toLowerCase();
    const foodNameDE = (food.nameDE || '').toLowerCase();
    const queryLower = query.toLowerCase().trim();
    const queryWords = queryLower.split(/\s+/).filter((w: string) => w.length > 0);
    
    // PRIORITY 1: Favorites get absolute priority (massive negative bonus)
    if (isFavorite(food.id)) {
      return -1000000 + (fuseScore * 0.01); // Favorites ALWAYS first
    }
    
    // PRIORITY 2: Custom foods and recipes
    if (food.isRecipe) {
      return -10000 + (fuseScore * 0.1);
    }
    if (food.isCustom) {
      return -5000 + (fuseScore * 0.1);
    }
    
    // PRIORITY 3: Database preference (Swiss > USDA > OFF)
    let databaseBonus = 0;
    if (preferences.language === 'de' && food.id.startsWith('swiss_')) {
      databaseBonus = -800; // Swiss foods get MASSIVE priority to outrank USDA bonuses
    } else if (food.id.startsWith('usda_')) {
      databaseBonus = -150; // USDA foods preferred
    } else if (food.id.startsWith('off_')) {
      databaseBonus = 100; // OFF foods strongly deprioritized
    }
    
    // PRIORITY 4: Multi-word query matching
    let multiWordBonus = 0;
    if (queryWords.length > 1) {
      const nameToCheck = preferences.language === 'de' ? (foodNameDE || foodNameEN) : (foodNameEN || foodNameDE);
      const foodWords = nameToCheck.split(/[\s,\-()]+/).filter((w: string) => w.length > 0);
      
      let matchedWords = 0;
      let exactMatches = 0;
      
      for (const queryWord of queryWords) {
        if (foodWords.some((w: string) => w === queryWord)) {
          matchedWords++;
          exactMatches++;
        } else if (foodWords.some((w: string) => w.startsWith(queryWord))) {
          matchedWords += 0.8;
        } else if (nameToCheck.includes(queryWord)) {
          matchedWords += 0.3; // Partial credit for substring matches
        }
      }
      
      // Strong bonus for matching all or most query words
      const matchRatio = matchedWords / queryWords.length;
      if (matchRatio >= 1.0) {
        multiWordBonus = -500 - (exactMatches * 100); // All words matched
      } else if (matchRatio >= 0.8) {
        multiWordBonus = -300 - (exactMatches * 50); // Most words matched
      } else if (matchRatio >= 0.5) {
        multiWordBonus = -100 - (exactMatches * 20); // Some words matched
      } else {
        multiWordBonus = 200; // Poor match for multi-word query
      }
    } else {
      // PRIORITY 4b: Single word matching
      const nameToCheck = preferences.language === 'de' ? (foodNameDE || foodNameEN) : (foodNameEN || foodNameDE);
      const words = nameToCheck.split(/[\s,\-()]+/).filter((w: string) => w.length > 0);
      
      // Exact word match gets MASSIVE bonus, with position-based priority
      const exactWordIndex = words.findIndex((w: string) => w === queryLower);
      if (exactWordIndex !== -1) {
        // Extra strong bonus for USDA items to compete with Swiss database preference
        const baseBonus = food.id.startsWith('usda_') ? -1500 : -1000;
        
        // HUGE additional bonus for words at the start of the name
        let positionBonus = 0;
        if (exactWordIndex === 0) {
          positionBonus = -500; // First word gets massive bonus
        } else if (exactWordIndex === 1) {
          positionBonus = -200; // Second word gets good bonus
        } else if (exactWordIndex === 2) {
          positionBonus = -100; // Third word gets small bonus
        }
        // Words after position 3 get no position bonus
        
        multiWordBonus = baseBonus + positionBonus;
      }
      // Word starts with query gets good bonus
      else if (words.some((w: string) => w.startsWith(queryLower))) {
        multiWordBonus = -200;
      }
      // Only appears in compound words gets penalty
      else if (nameToCheck.includes(queryLower)) {
        multiWordBonus = 100; // Stronger penalty for compound-only matches
      }
    }
    
    // Simple final score calculation (lower = better ranking)
    return fuseScore + databaseBonus + multiWordBonus;
  }, [preferences.language, isFavorite]);

  // Get custom foods and recipes for dependency tracking
  const customFoods = getCustomFoods();
  const savedRecipes = getRecipes();

  // Build word index when foods data changes
  useEffect(() => {
    // Include all searchable items in the word index
    const customFoodItems = customFoods.map(customFood => ({
      id: customFood.id,
      nameEN: customFood.name,
      nameDE: customFood.name
    }));
    
    const savedRecipeItems = savedRecipes.map(recipe => ({
      id: recipe.id,
      nameEN: recipe.name,
      nameDE: recipe.name
    }));
    
    const allSearchableFoods = [...foods, ...customFoodItems, ...savedRecipeItems];
    
    if (allSearchableFoods.length > 0) {
      buildWordIndex(allSearchableFoods as Food[]);
    }
  }, [foods, customFoods, savedRecipes, buildWordIndex]);

  // Clear cache when foods data or favorites change
  useEffect(() => {
    searchCache.current.clear();
  }, [foods, customFoods.length, savedRecipes.length, preferences.favorites.length]);

  // Check camera availability on component mount
  useEffect(() => {
    const checkCameraAvailability = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setIsCameraAvailable(false);
          return;
        }
        
        // Check if camera permission was previously granted/denied
        if (navigator.permissions) {
          const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          setCameraPermission(permission.state === 'granted' ? 'granted' : permission.state === 'denied' ? 'denied' : 'unknown');
        }
      } catch (error) {
        setIsCameraAvailable(false);
      }
    };
    
    checkCameraAvailability();
  }, []);

  const handleBarcodeScanned = useCallback((barcode: string) => {
    setQuery(barcode);
    setIsScannerOpen(false);
  }, []);

  const handleScannerOpen = () => {
    setIsScannerOpen(true);
  };

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      // Check for barcode search - handle this first and bypass caching
      const isBarcodeSearch = debouncedQuery.toLowerCase().startsWith('barcode:');
      if (isBarcodeSearch) {
        const barcodeValue = debouncedQuery.slice(8); // Remove 'barcode:' prefix
        if (barcodeValue.trim()) {
          console.log('Searching for barcode:', barcodeValue.trim());
          console.log('Available foods count:', foods.length);
          
          // Custom foods don't have EAN codes, so only search database foods
          console.log('Total database foods:', foods.length);
          
          // Search for exact EAN match in database foods only
          const barcodeResults = foods.filter(food => {
            const hasEan = food.ean && food.ean === barcodeValue.trim();
            if (hasEan) {
              console.log('Found matching food:', food.nameEN || food.nameDE, 'EAN:', food.ean);
            }
            return hasEan;
          });
          
          console.log('Barcode search results:', barcodeResults.length);
          setSearchResults(barcodeResults);
          setCurrentPage(1);
          onSearchStarted?.();
          return;
        }
      }
      
      // Check cache first
      const cacheKey = `${debouncedQuery}_${preferences.language}`;
      const cachedResults = searchCache.current.get(cacheKey);
      
      if (cachedResults) {
        setSearchResults(cachedResults);
        setCurrentPage(1);
        onSearchStarted?.();
        return;
      }
      
      // Get all foods that would be in the fuse instance (same logic as original fuse creation)
      const customFoodItems = customFoods.map(customFood => {
        const standardPortion = { descEN: '100g', descDE: '100g', grams: 100 };
        const customPortions = (customFood.portions || []).map(portion => ({
          descEN: portion.name,
          descDE: portion.name,
          grams: portion.grams
        }));
        
        return {
          id: customFood.id,
          nameEN: customFood.name,
          nameDE: customFood.name,
          carbs: customFood.carbs,
          protein: customFood.protein,
          fat: customFood.fat,
          portions: [standardPortion, ...customPortions],
          source: preferences.language === 'de' ? 'Eigener Eintrag' : 'Own Entry',
          sourceDE: 'Eigener Eintrag',
          isCustom: true
        };
      });

      const savedRecipeItems = savedRecipes.map(recipe => {
        const totals = recipe.ingredients.reduce((acc, ingredient) => ({
          carbs: acc.carbs + ingredient.carbs,
          protein: acc.protein + ingredient.protein,
          fat: acc.fat + ingredient.fat,
          grams: acc.grams + ingredient.grams
        }), { carbs: 0, protein: 0, fat: 0, grams: 0 });

        const recipePortions = recipe.portions.map(portion => ({
          descEN: portion.name,
          descDE: portion.name,
          grams: portion.grams
        }));

        const recipeFood = {
          id: recipe.id,
          nameEN: recipe.name,
          nameDE: recipe.name,
          carbs: totals.grams > 0 ? (totals.carbs / totals.grams) * 100 : 0,
          protein: totals.grams > 0 ? (totals.protein / totals.grams) * 100 : 0, 
          fat: totals.grams > 0 ? (totals.fat / totals.grams) * 100 : 0,
          portions: recipePortions,
          source: preferences.language === 'de' ? 'Eigenes Rezept' : 'Own Recipe',
          sourceDE: 'Eigenes Rezept',
          isCustom: true,
          isRecipe: true
        };
        
        // Debug logging for recipe conversion
        if (recipe.name.toLowerCase().includes(debouncedQuery.toLowerCase())) {
          console.log('Recipe found in search:', recipe.name, 'Totals:', totals, 'Per 100g:', recipeFood);
        }
        
        return recipeFood;
      });

      const filteredDatabaseFoods = foods.filter(food => {
        if (food.isCustom || food.isRecipe) return true;
        // Check if food has a name in the specified language
        const name = preferences.language === 'de' ? food.nameDE : food.nameEN;
        return Boolean(name && name.trim());
      });
      
      const allFoods = [...customFoodItems, ...savedRecipeItems, ...filteredDatabaseFoods];
      
      // Debug logging for recipe inclusion
      if (savedRecipeItems.length > 0) {
        console.log('Including saved recipes in search:', savedRecipeItems.length, 'recipes');
      }
      
      // Pre-filter foods using word index for better performance
      const preFilteredFoods = getPreFilteredFoods(debouncedQuery, allFoods);
      
      // Create Fuse instance with pre-filtered foods
      const enhancedPreFilteredFoods = preFilteredFoods.map(food => ({
        ...food,
        searchableNames: [food.nameEN, food.nameDE].filter(Boolean)
      }));
      
      const filteredFuse = new Fuse(enhancedPreFilteredFoods, {
        keys: ['searchableNames'],
        threshold: 0.4,
        includeScore: true,
        minMatchCharLength: 1,
        ignoreLocation: true,
        useExtendedSearch: false
      });
      
      // Use higher limit to avoid missing important items with exact word matches
      const results = filteredFuse.search(debouncedQuery, { limit: 300 });
      
      // CRITICAL FIX: Manually inject favorite foods that match the query
      // because Fuse.js might not find them due to threshold settings
      const favoriteMatches = allFoods.filter(food => {
        if (!isFavorite(food.id)) return false;
        
        const nameToCheck = preferences.language === 'de' ? 
          (food.nameDE || food.nameEN || '').toLowerCase() : 
          (food.nameEN || food.nameDE || '').toLowerCase();
        
        const queryWords = debouncedQuery.toLowerCase().split(/\s+/).filter((w: string) => w.length > 0);
        
        // Check if favorite contains any query words
        return queryWords.some(word => nameToCheck.includes(word));
      });
      
      // Add favorites to results if they're not already there
      const resultIds = new Set(results.map((r: any) => r.item.id));
      const injectedFavorites = favoriteMatches
        .filter(food => !resultIds.has(food.id))
        .map(food => ({
          item: food,
          score: 0.5 // Good score for manual injection
        }));
      
      // ADDITIONAL FIX: Inject important items with exact word matches that Fuse.js might still miss
      let importantMatches: any[] = [];
      const queryWords = debouncedQuery.toLowerCase().split(/\s+/).filter((w: string) => w.length > 0);
      
      // For single word queries, find items with exact word matches
      if (queryWords.length === 1 && queryWords[0].length > 2) {
        const allResultIds = new Set([...results.map((r: any) => r.item.id), ...injectedFavorites.map(r => r.item.id)]);
        
        importantMatches = allFoods.filter(food => {
          if (allResultIds.has(food.id)) return false; // Already in results
          
          const nameToCheck = preferences.language === 'de' ? 
            (food.nameDE || food.nameEN || '').toLowerCase() : 
            (food.nameEN || food.nameDE || '').toLowerCase();
          
          const words = nameToCheck.split(/[\s,\-()]+/).filter((w: string) => w.length > 0);
          
          // Find items with exact word matches that Fuse.js missed
          return words.some((w: string) => w === queryWords[0]);
        }).slice(0, 20).map(food => ({
          item: food,
          score: 0.3 // Good score for exact word matches
        }));
      }

      const allResults = [...results, ...injectedFavorites, ...importantMatches];
      
      // Sort results using our custom ranking
      const rankedResults = allResults
        .map(result => ({
          food: result.item,
          score: rankFood(result, debouncedQuery)
        }))
        .sort((a, b) => a.score - b.score) // Lower score = better ranking
        .map(item => item.food);
      
      // Cache the results
      if (searchCache.current.size >= maxCacheSize) {
        // Remove oldest entry (LRU eviction)
        const firstKey = searchCache.current.keys().next().value;
        searchCache.current.delete(firstKey);
      }
      searchCache.current.set(cacheKey, rankedResults);
      
      setSearchResults(rankedResults);
      setCurrentPage(1);
      onSearchStarted?.();
    } else {
      setSearchResults([]);
      setCurrentPage(1);
      onSearchCleared?.();
    }
  }, [debouncedQuery, foods, getCustomFoods, getRecipes, preferences.language, onSearchStarted, onSearchCleared, rankFood, maxCacheSize, getPreFilteredFoods, customFoods, savedRecipes, isFavorite]);

  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * resultsPerPage;
    setDisplayedResults(searchResults.slice(startIndex, endIndex));
    
    // Scroll to top when results are displayed OR when no results are shown (especially important for paste operations)
    if ((searchResults.length > 0 && currentPage === 1) || (debouncedQuery.length > 0 && searchResults.length === 0)) {
      // Use setTimeout to ensure DOM has updated with new results
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  }, [searchResults, currentPage, resultsPerPage, debouncedQuery]);

  const loadMore = () => {
    if (displayedResults.length < searchResults.length) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const hasResults = searchResults.length > 0;
  const hasMoreResults = displayedResults.length < searchResults.length;
  const hasQuery = debouncedQuery.length > 0;
  const showNoResults = hasQuery && !hasResults;

  return (
    <div className="search-container">
      <div className="search-input-container">
        <input
          type="text"
          className="search-input"
          placeholder={preferences.language === 'de' ? 'Lebensmittel suchen...' : 'Search food...'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Dismiss keyboard when Enter is pressed
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          onFocus={(e) => {
            // Scroll input into view when focused (especially on mobile)
            setTimeout(() => {
              e.target.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
            }, 300); // Longer delay to wait for keyboard animation
          }}
        />
        <button
          className={`barcode-button ${
            !isCameraAvailable ? 'disabled' : 
            cameraPermission === 'denied' ? 'no-permission' : ''
          }`}
          onClick={handleScannerOpen}
          disabled={!isCameraAvailable}
          title={preferences.language === 'de' ? 'Barcode scannen' : 'Scan barcode'}
        >
          {cameraPermission === 'denied' ? (
            <span className="barcode-icon blocked">
              <svg width="20" height="16" viewBox="0 0 20 16" fill="currentColor">
                <rect x="0" y="0" width="1" height="16"/>
                <rect x="2" y="0" width="2" height="16"/>
                <rect x="5" y="0" width="1" height="16"/>
                <rect x="7" y="0" width="1" height="16"/>
                <rect x="9" y="0" width="2" height="16"/>
                <rect x="12" y="0" width="1" height="16"/>
                <rect x="14" y="0" width="1" height="16"/>
                <rect x="16" y="0" width="2" height="16"/>
                <rect x="19" y="0" width="1" height="16"/>
              </svg>
            </span>
          ) : (
            <span className="barcode-icon">
              <svg width="20" height="16" viewBox="0 0 20 16" fill="currentColor">
                <rect x="0" y="0" width="1" height="16"/>
                <rect x="2" y="0" width="2" height="16"/>
                <rect x="5" y="0" width="1" height="16"/>
                <rect x="7" y="0" width="1" height="16"/>
                <rect x="9" y="0" width="2" height="16"/>
                <rect x="12" y="0" width="1" height="16"/>
                <rect x="14" y="0" width="1" height="16"/>
                <rect x="16" y="0" width="2" height="16"/>
                <rect x="19" y="0" width="1" height="16"/>
              </svg>
            </span>
          )}
        </button>
      </div>
      
      {hasResults && (
        <AdvancedOptions
          carbUnit={preferences.carbUnit}
          onUnitChange={onUnitChange}
          customPortionGrams={customPortionGrams}
          onCustomPortionChange={setCustomPortionGrams}
          language={preferences.language}
          showFPU={preferences.showFPU}
          onShowFPUChange={onShowFPUChange}
        />
      )}
      
      {isScannerOpen && (
        <Suspense fallback={<div>Loading scanner...</div>}>
          <BarcodeScanner
            isOpen={isScannerOpen}
            onClose={() => setIsScannerOpen(false)}
            onBarcodeScanned={handleBarcodeScanned}
            language={preferences.language}
          />
        </Suspense>
      )}
      
      {showNoResults && (
        <div className="no-results">
          <div className="no-results-icon">
            <img src="/sad_bowl.png" alt="No results" className="sad-bowl-image" />
          </div>
          <p className="no-results-text">
            {preferences.language === 'de' 
              ? 'Leider keine guten Ergebnisse gefunden... :(' 
              : 'Unfortunately no good results found... :('}
          </p>
          <p className="no-results-hint">
            {preferences.language === 'de'
              ? 'Versuche einen anderen Suchbegriff oder scanne einen anderen Barcode.'
              : 'Try a different search term or scan another barcode.'}
          </p>
        </div>
      )}
      
      {hasResults && (
        <>
          <SearchResults
            foods={displayedResults}
            carbUnit={preferences.carbUnit}
            customPortionGrams={customPortionGrams}
            language={preferences.language}
            showFPU={preferences.showFPU}
          />
          
          {hasMoreResults && (
            <button className="load-more-button" onClick={loadMore}>
              {preferences.language === 'de' ? 'Mehr laden' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
};